import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';

import FormGroup from '../../../../../../../../../../../components/global/FormGroup';
import Button from '../../../../../../../../../../../components/global/Button';
import Avatar from '../../../../../../../../../../../components/global/Avatar';
import { AVATAR_SIZE_72 } from '../../../../../../../../../../../constants/avatar-sizes';
import DismissButton from '../../../../../../../../../../../components/global/DismissButton';
import PrintProductSizeRoesConfiguratorSampleModal from './components/PrintProductSizeRoesConfiguratorSample';
import CardElementPhotoSelectionControl from '../../../../../../../../../../../services/cards/components/CardElementPhoto/CardElementPhotoSelectionControl';
import { breakpointDown } from '../../../../../../../../../../../services/responsive/responsive';
import { PHOTO_SELECTION_CONTROL_FACES_OPTIONS } from '../../../../../../../../../../../services/cards/components/CardElementPhoto/CardElementPhotoSelectionControl/constants';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string,
    thumbnail_urls: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
  }).isRequired,
  printProduct: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  printProductSize: PropTypes.shape({
    id: PropTypes.string,
    print_size: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    roes_info: PropTypes.shape({
      text_nodes: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          ui_label: PropTypes.string,
          is_required: PropTypes.bool,
        }),
      ),
      image_nodes: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          ui_label: PropTypes.string,
          is_required: PropTypes.bool,
        }),
      ),
    }),
  }).isRequired,
  albumFolderTeamTag: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    last_name: PropTypes.string,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  defaultConfiguration: PropTypes.object,
  isInCart: PropTypes.bool,
  isAllPhotos: PropTypes.bool,
  onComplete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
const PrintProductSizeRoesConfigurator = ({
  photo,
  printProduct,
  printProductSize,
  identity,
  albumMembershipGroup,
  defaultConfiguration = {
    player_name: `${identity?.name ?? ''} ${identity?.last_name ?? ''}`.trim(),
    player_name_copy: `${identity?.name ?? ''} ${
      identity?.last_name ?? ''
    }`.trim(),
    year: new Date().getFullYear(),
  },
  isInCart,
  isAllPhotos,
  onComplete,
  onClose,
}) => {
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const [viewingSample, setViewingSample] = useState(false);
  const [selectingPhoto, setSelectingPhoto] = useState(false);
  const [photoTarget, setPhotoTarget] = useState(null);
  const previewImage = printProduct?.preview_asset_image?.url;
  const {
    print_size: printSize,
    roes_info: { image_nodes: imageNodes = [], text_nodes: textNodes = [] },
  } = printProductSize;

  const isTeamPhoto = useMemo(() => {
    if (!photo?.price_album_folder_tag) {
      return false;
    }
    return (
      (photo?.price_album_folder_tag?.name ?? '')
        ?.toLowerCase()
        ?.includes('team') ||
      (photo?.price_album_folder_tag?.name ?? '')
        ?.toLowerCase()
        ?.includes('group')
    );
  }, [photo]);

  const initialFormValues = useMemo(() => {
    const values = {};

    imageNodes.forEach(node => {
      if (defaultConfiguration?.[node.id]) {
        values[node.id] = defaultConfiguration[node.id];
        return;
      }

      if (
        !isTeamPhoto &&
        node.id.includes('individual_image') &&
        (!photo?.faces || photo?.faces?.total === 1)
      ) {
        values[node.id] = photo;
        return;
      }

      if (node.id.includes('team_image') && isTeamPhoto) {
        values[node.id] = photo;
        return;
      }

      values[node.id] = null;
    });

    textNodes.forEach(node => {
      if (defaultConfiguration?.[node.id]) {
        values[node.id] = defaultConfiguration[node.id];
        return;
      }

      values[node.id] = '';
    });

    return values;
  }, [imageNodes, textNodes, defaultConfiguration]);

  //
  const editableImageNodes = useMemo(
    () => imageNodes.filter(node => !node.id.includes('_copy')),
    [imageNodes],
  );

  const editableTextNodes = useMemo(
    () => textNodes.filter(node => !node.id.includes('_copy')),
    [textNodes],
  );

  const onFormSubmit = values => {
    onComplete({ printProduct, printProductSize, roesConfiguration: values });
  };

  const { values, handleSubmit, handleChange } = useFormik({
    initialValues: initialFormValues,
    onSubmit: onFormSubmit,
  });

  const isFormSubmittable = useMemo(() => {
    return !Object.keys(values).find(key => !values[key]);
  }, [values]);

  return (
    <Fragment>
      <div className="print-product-size-roes-configurator-close-button">
        <DismissButton onClick={onClose} absolute />
      </div>
      <div className="text-center">
        <h4>Configure your {printSize.name}</h4>
        {previewImage && (
          <Fragment>
            <Button
              btnStyle="link"
              btnSize="xs"
              onClick={() => setViewingSample(true)}
            >
              SEE SAMPLE IMAGE &rarr;
            </Button>{' '}
          </Fragment>
        )}
      </div>
      <hr className="mt-1" />
      <div className="print-product-size-roes-configurator-container">
        <div className="print-product-size-roes-configurator-form">
          <div className="image-nodes-container">
            {editableImageNodes.map(node => (
              <div
                key={node.id}
                data-testid={node.id}
                role="button"
                tabIndex={0}
                className={classNames({
                  'image-node image-node-editable': true,
                })}
                onClick={() => {
                  setPhotoTarget(node.id);
                  setSelectingPhoto(true);
                }}
              >
                <Avatar
                  photo={values[node.id]}
                  size={AVATAR_SIZE_72}
                  className="mb-1"
                />
                {isTeamPhoto &&
                editableImageNodes.length === 1 &&
                editableImageNodes[0].id.includes('individual_image')
                  ? `${isMobile ? 'TAP' : 'CLICK'} TO ADD YOUR INDIVIDUAL PHOTO`
                  : node.ui_label.toUpperCase()}
              </div>
            ))}
          </div>
          <div className="text-nodes-container">
            {editableTextNodes.map(node => (
              <FormGroup
                key={node.id}
                id={node.id}
                label={node.ui_label}
                value={values[node.id]}
                labelType="expanded"
                disabled={!!node.default_text}
                onChange={e => {
                  handleChange(e);

                  if (Object.keys(values).includes(`${node.id}_copy`)) {
                    handleChange({
                      target: {
                        name: `${node.id}_copy`,
                        value: e.target.value,
                      },
                    });
                  }
                }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="text-center mt-2">
        <Button onClick={handleSubmit} disabled={!isFormSubmittable}>
          {isInCart ? 'Save changes' : 'Add to cart'}
        </Button>
      </div>
      <PrintProductSizeRoesConfiguratorSampleModal
        forceOpen={viewingSample}
        printProduct={printProduct}
        printSize={printSize}
        onClose={() => setViewingSample(false)}
      />
      <CardElementPhotoSelectionControl
        uploadDisabled
        photosContext={{
          album_id:
            albumMembershipGroup &&
            (albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          album_folder_id:
            albumMembershipGroup &&
            (!albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          root_album_folder_id:
            albumMembershipGroup && albumMembershipGroup.is_album
              ? albumMembershipGroup.album.root_album_folder?.id
              : undefined,
          // We rely on a resolver that the membershipGroup would have by
          // default when we don't create a pseudo membership (identities).
          // This allows us to actually discern if this is a membership or not.
          is_album_membership_group: !!albumMembershipGroup?.identities,
          is_all_photos: isAllPhotos,
        }}
        forceOpen={selectingPhoto}
        facesOptionDefault={(() => {
          if (isTeamPhoto || (photoTarget ?? '').includes('team')) {
            return PHOTO_SELECTION_CONTROL_FACES_OPTIONS.MULTIPLE;
          }

          if ((photoTarget ?? '').includes('individual')) {
            return PHOTO_SELECTION_CONTROL_FACES_OPTIONS.INDIVIDUAL;
          }

          return PHOTO_SELECTION_CONTROL_FACES_OPTIONS.ALL;
        })()}
        onClose={() => {
          setPhotoTarget(null);
          setSelectingPhoto(false);
        }}
        onRecordChange={matchedPhoto => {
          if (matchedPhoto?.__typename === 'Photo') {
            handleChange({
              target: {
                name: editableImageNodes.find(({ id }) => photoTarget === id)
                  .id,
                value: matchedPhoto,
              },
            });
            const copyNode = imageNodes.find(
              ({ id }) => id === `${photoTarget}_copy`,
            );
            if (copyNode) {
              handleChange({
                target: {
                  name: copyNode.id,
                  value: matchedPhoto,
                },
              });
            }
          } else {
            handleChange({
              target: {
                name: editableImageNodes.find(({ id }) => photoTarget === id)
                  .id,
                value: matchedPhoto.photo,
              },
            });
            const copyNode = imageNodes.find(
              ({ id }) => id === `${photoTarget}_copy`,
            );
            if (copyNode) {
              handleChange({
                target: {
                  name: copyNode.id,
                  value: matchedPhoto,
                },
              });
            }
          }
          setPhotoTarget(null);
          setSelectingPhoto(false);
        }}
      />
    </Fragment>
  );
};

PrintProductSizeRoesConfigurator.propTypes = propTypes;

export default PrintProductSizeRoesConfigurator;
